import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, input, output, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { map, startWith, switchMap } from 'rxjs';
import { ConsoleSummaryMarkdownComponent } from '../../components/console-summary-markdown/console-summary-markdown.component';
import { StepForm } from '../../../../../services/workflow-engine/generated/models/step-form';
import {
  isFieldRequired,
  isFieldVisible,
  labelFor,
  missingField,
  outcomeNeedsReason,
  RETURNED_OUTCOME as RETURNED,
  roleLabel,
  unsupportedField,
  type FormFieldView,
} from '../engine-case.mapper';

export interface StepDecision {
  outcome: string;
  values: Record<string, string>;
  reason: string;
}

export interface StepCandidateOption {
  id: string;
  label: string;
  family?: string | null;
  rationale?: string | null;
  rank?: number;
  recommendedActions?: readonly { id: string; description: string }[];
}

/** The step the engine says is active: its role, owner, captured fields, and outcomes, rendered from the step form. */
@Component({
  selector: 'app-engine-step-panel',
  imports: [NgTemplateOutlet, ReactiveFormsModule, ConsoleSummaryMarkdownComponent],
  templateUrl: './engine-step-panel.component.html',
  styleUrls: [
    '../../workflow-detail.scss',
    './engine-step-panel.component.scss',
  ],
})
export class EngineStepPanelComponent {
  form = input.required<StepForm>();
  fields = input.required<readonly FormFieldView[]>();
  candidates = input<readonly StepCandidateOption[]>([]);
  presentation = input<'default' | 'uc2'>('default');
  embedded = input(false);
  actionDescriptionShown = input(false);
  enforceUc2Decisions = input(false);
  claimOnSubmit = input(false);
  allowReturnAndRelease = input(true);
  allowRelease = input(true);
  initialValues = input<Record<string, string>>({});
  canClaim = input(false);
  canDecide = input(false);
  isOwner = input(false);
  /** Name of the current owner, when someone other than the viewer owns the step. */
  ownerName = input<string | null>(null);
  /** Name of the person a return goes back to, when the audit names one. */
  returnToName = input<string | null>(null);
  busy = input(false);
  commandError = input<string | null>(null);

  claim = output<void>();
  decide = output<StepDecision>();
  release = output<string>();

  readonly labelFor = labelFor;
  returnTarget = computed(() => labelFor(this.form().return?.to ?? '').toLowerCase().replace(/ gate$/, ''));

  private _reason = new FormControl('', { nonNullable: true });
  reasonControl = this._reason;

  /** One control per field; the group is kept while the same step's fields are re-read, so typing survives a reload. */
  private _group = computed(
    () => {
      const fields = this.fields();
      const controls: Record<string, FormControl<string>> = Object.fromEntries(
        fields.map(field => [field.key, new FormControl(this.initialValues()[field.key] ?? '', { nonNullable: true })]),
      );
      return {
        id: `${this.form().stepId}|${fields.map(field => field.key).join(',')}`,
        group: new FormGroup(controls),
      };
    },
    { equal: (a, b) => a.id === b.id },
  );
  controls = computed(() => this._group().group.controls);

  private _values = toSignal(
    toObservable(this._group).pipe(
      switchMap(({ group }) =>
        group.valueChanges.pipe(
          startWith(group.getRawValue()),
          map(() => group.getRawValue() as Record<string, string>),
        ),
      ),
    ),
    { initialValue: {} as Record<string, string> },
  );
  private _reasonValue = toSignal(this._reason.valueChanges.pipe(startWith('')), { initialValue: '' });
  private _resetKey = computed(() => this.form().stepId);
  private _uc2AnalystDecision = computed(() =>
    (this.enforceUc2Decisions() || this.presentation() === 'uc2') && this.form().stepKey === 'analyst_gate',
  );

  private _analystCandidateField = computed(() =>
    this.presentation() === 'uc2' && this.form().stepKey === 'analyst_gate'
      ? this.fields().find(field => field.name === 'selectedCandidate' && field.type === 'ref')
      : undefined,
  );
  private _reclassification = computed(() => {
    const candidateField = this._analystCandidateField();
    if (!candidateField) return null;
    const field = this.fields().find(entry => entry.record === candidateField.record &&
      entry.name === 'reclassReason' && entry.type === 'text');
    if (!field) return null;
    const values = this._values();
    if (isFieldRequired(field, values)) return null;
    const assessment = values[`${field.record}__assessedAssessment`];
    if (values[`${field.record}__validity`] !== 'Real' || !assessment) {
      return { field, required: false };
    }
    const family = this.candidates().find(candidate => candidate.id === values[candidateField.key])?.family;
    return family ? { field, required: assessment !== family } : null;
  });

  roleName = computed(() => roleLabel(this.form().role));
  visibleFields = computed(() => {
    const candidateField = this._analystCandidateField();
    const reclassification = this._reclassification();
    const values = this._values();
    const fields = this.fields().filter(field => {
      const optionalClaimReason = this.presentation() === 'uc2' && this.form().stepKey === 'analyst_gate' &&
        field.record === 'analyst_decision' && field.name === 'claimReason' && !isFieldRequired(field, values);
      const visible = field === reclassification?.field ? reclassification.required : isFieldVisible(field, values);
      return field !== candidateField && field.type !== 'other' && !optionalClaimReason && visible;
    });
    if (!candidateField) return fields;
    const validityField = fields.find(field => field.record === candidateField.record && field.name === 'validity');
    return validityField
      ? [candidateField, validityField, ...fields.filter(field => field !== validityField)]
      : [candidateField, ...fields];
  });
  decisionOutcomes = computed(() => {
    const outcomes = this.form().outcomes.filter(outcome => outcome !== RETURNED);
    if (!this._uc2AnalystDecision()) return outcomes;
    const validityField = this.fields().find(field =>
      field.record === 'analyst_decision' && field.name === 'validity' && field.type === 'vocab',
    );
    if (!validityField) return outcomes;
    const validity = this._values()[validityField.key];
    return outcomes.filter(outcome => {
      if (outcome === 'confirmed') return validity === 'Real';
      if (outcome === 'dismissed') return validity === 'NotReal';
      return true;
    });
  });
  canReturn = computed(() => this.allowReturnAndRelease() && this.form().return !== null);
  canRelease = computed(() => this.allowReturnAndRelease() && this.allowRelease() && this.presentation() !== 'uc2' && this.isOwner());
  item = computed(() => this.form().item);
  reasonBlank = computed(() => !this._reasonValue().trim());
  recordActionBlockedByReason = computed(() =>
    this.presentation() === 'uc2' && this.form().stepKey === 'execute_actions' && !this.reasonBlank(),
  );
  submitClaims = computed(() => this.claimOnSubmit() || this.presentation() === 'uc2');

  ownerLabel = computed(() => {
    if (this.isOwner()) {
      return 'Owned by you';
    }
    if (this.form().owner) {
      return `Owned by ${this.ownerName() ?? 'another user'}`;
    }
    // Responding claims an unowned later step, so the viewer can act without claiming first.
    return this.canDecide()
      ? `Waiting in the ${this.roleName()} queue — open the step to respond`
      : `Waiting in the ${this.roleName()} queue`;
  });

  /** Why the decision outcomes cannot be sent yet, or null when they can. */
  blocked = computed<string | null>(() => {
    const values = this._values();
    const unsupported = unsupportedField(this.fields(), values);
    if (unsupported) {
      return `${unsupported.label} needs structured input this screen cannot capture yet.`;
    }
    const reclassification = this._reclassification();
    const missing = missingField(this.fields().filter(field => field !== reclassification?.field), values) ??
      (reclassification?.required && !(values[reclassification.field.key] ?? '').trim() ? reclassification.field : null);
    return missing ? `${missing.label} is required.` : null;
  });
  validationMessage = computed(() =>
    this.presentation() === 'uc2' && !unsupportedField(this.fields(), this._values()) ? null : this.blocked(),
  );
  private _attemptedOutcome = signal<string | null>(null);
  /** Shown after a reason-only outcome is pressed while required fields are still empty; clears as they are filled. */
  submitBlock = computed(() => (this._attemptedOutcome() ? this.blocked() : null));
  confirmationBlock = computed(() => {
    if (!this._uc2AnalystDecision()) return null;
    const values = this._values();
    return values['analyst_decision__validity'] === 'Real' &&
      values['analyst_decision__assessedAssessment'] === 'PressureDisturbanceCauseUnclear'
      ? 'Select a valid Class assessment before confirming and sending for approval. "Pressure disturbance cause unclear" is not a valid class selection.'
      : null;
  });
  returnNeedsReason = computed(() => outcomeNeedsReason(RETURNED, this.form()));
  reasonActions = computed(() => {
    const actions: string[] = [];
    if (this.canRelease()) actions.push('release to queue');
    if (this.canReturn() && this.returnNeedsReason()) actions.push('return');
    for (const outcome of this.decisionOutcomes()) {
      if (outcomeNeedsReason(outcome, this.form())) actions.push(this.actionLabel(outcome).toLowerCase());
    }
    return actions;
  });
  /** A claim is waiting for the engine's answer and the re-read. */
  claimPending = computed(() => this.canClaim() && this.busy());

  constructor() {
    effect(() => {
      this._resetKey();
      this._reason.reset('');
      this._attemptedOutcome.set(null);
    });
    effect(() => {
      const field = this._analystCandidateField();
      const candidates = this.candidates();
      if (!field || !candidates.length) return;
      const control = this.controls()[field.key];
      if (!control.value) control.setValue(candidates[0].id);
      const candidate = candidates.find(entry => entry.id === control.value);
      if (candidate) this.defaultAssessment(field, candidate);
    });
  }

  isRequired(field: FormFieldView): boolean {
    const reclassification = this._reclassification();
    return field === reclassification?.field ? reclassification.required : isFieldRequired(field, this._values());
  }

  actionLabel(outcome: string): string {
    if (this.presentation() !== 'uc2') return labelFor(outcome);
    const labels: Record<string, string> = {
      confirmed: 'Confirm and send for approval', dismissed: 'Dismiss case',
      approved: 'Approve resolution', done: 'Record action',
    };
    return labels[outcome] ?? labelFor(outcome);
  }

  selectCandidate(field: FormFieldView, candidate: StepCandidateOption): void {
    if (this.busy() || (!this.canClaim() && !this.canDecide())) return;
    this.controls()[field.key].setValue(candidate.id);
    const validity = this.fields().find(entry => entry.record === field.record && entry.name === 'validity');
    if (validity?.values.includes('Real')) this.controls()[validity.key].setValue('Real');
    this.defaultAssessment(field, candidate);
    this.onInteraction();
  }

  /** A decision outcome is unavailable until the viewer can decide, while a field is missing, or when the engine needs a reason that is blank. */
  outcomeDisabled(outcome: string): boolean {
    const canSubmit = this.canDecide() || (this.submitClaims() && this.canClaim());
    return (
      !this.decisionOutcomes().includes(outcome) || !canSubmit || this.busy() || this._fieldsBlock(outcome) ||
      (outcome === 'done' && this.recordActionBlockedByReason()) ||
      (outcome === 'confirmed' && !!this.confirmationBlock()) ||
      (outcomeNeedsReason(outcome, this.form()) && this.reasonBlank())
    );
  }

  /** An outcome that needs a reason is enabled by the reason alone; its missing required fields are reported when it is pressed. */
  private _fieldsBlock(outcome: string): boolean {
    if (!this.blocked()) return false;
    return !outcomeNeedsReason(outcome, this.form()) || !!unsupportedField(this.fields(), this._values());
  }

  /** UC2 keeps edits local; other presentations retain their first-edit claiming policy. */
  onInteraction(): void {
    if (!this.submitClaims() && this.canClaim() && !this.busy()) {
      this.claim.emit();
    }
  }

  onDecide(outcome: string): void {
    if (this.outcomeDisabled(outcome)) {
      return;
    }
    if (this.blocked()) {
      this._attemptedOutcome.set(outcome);
      return;
    }
    const values = { ...this._values() };
    const reclassification = this._reclassification();
    if (reclassification && !reclassification.required) delete values[reclassification.field.key];
    this.decide.emit({ outcome, values, reason: this._reasonValue() });
  }

  onReturn(): void {
    if (!this.canReturn() || !this.canDecide() || this.busy() || (this.returnNeedsReason() && this.reasonBlank())) {
      return;
    }
    this.decide.emit({ outcome: RETURNED, values: {}, reason: this._reasonValue() });
  }

  onRelease(): void {
    if (this.canRelease() && !this.busy() && !this.reasonBlank()) {
      this.release.emit(this._reasonValue());
    }
  }

  private defaultAssessment(field: FormFieldView, candidate: StepCandidateOption): void {
    const assessment = this.fields().find(entry => entry.record === field.record &&
      entry.name === 'assessedAssessment' && entry.type === 'vocab');
    if (!assessment) return;
    const control = this.controls()[assessment.key];
    const useDefault = !control.value || (this.presentation() === 'uc2' && this.form().stepKey === 'analyst_gate' &&
      !control.dirty && !this.initialValues()[assessment.key]);
    const recommendation = candidate.family && assessment.values.includes(candidate.family) ? candidate.family : '';
    if (useDefault && control.value !== recommendation) control.setValue(recommendation);
  }
}
